<template>
  <div
    class="group relative h-128 md:h-100 lg:h-152"
    @mouseenter="pause"
    @mouseleave="resume"
    @focusin="pause"
    @focusout="resume"
  >
    <!-- slides: 3 positioned (left/center/right), rest hidden -->
    <div class="relative h-full overflow-hidden z-0">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="absolute inset-0"
        :class="slideClass(index)"
      >
        <home-page-blockbuster-slide
          :item="item"
          :is-active="index === activeIndex"
        />
      </div>
    </div>

    <!-- indicators -->
    <div
      v-if="items.length > 1"
      class="absolute bottom-6 md:bottom-10 lg:bottom-14 left-0 right-0 z-10 flex justify-between sm:justify-center sm:gap-8 px-4"
    >
      <button
        v-for="(icon, index) in indicatorIcons"
        :key="index"
        type="button"
        :data-testid="`blockbuster-dot-${index}`"
        :class="index === activeIndex ? 'cursor-default' : 'cursor-pointer'"
        :aria-label="`Go to slide ${index + 1}`"
        @click="slideTo(index)"
      >
        <common-svg-icon
          class="text-rhino transition-opacity duration-500 hover:opacity-100"
          :class="index === activeIndex ? 'opacity-100' : 'opacity-50'"
          :icon="icon"
          alt="indicator"
          :width="20"
          :height="20"
          :is-highlighted="index === activeIndex"
        />
      </button>
    </div>

    <!-- prev / next controls -->
    <template v-if="items.length > 1">
      <button
        aria-label="Previous"
        data-testid="blockbuster-prev"
        class="absolute top-1/2 left-4 -translate-y-1/2 z-10 hidden lg:flex items-center justify-center p-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        type="button"
        @click="prev"
      >
        <fa-icon
          class="text-rhino text-2xl drop-shadow size-6"
          :icon="faChevronLeft"
          aria-hidden="true"
        />
      </button>

      <button
        aria-label="Next"
        data-testid="blockbuster-next"
        class="absolute top-1/2 right-4 -translate-y-1/2 z-10 hidden lg:flex items-center justify-center p-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        type="button"
        @click="next"
      >
        <fa-icon
          class="text-rhino text-2xl drop-shadow size-6"
          :icon="faChevronRight"
          aria-hidden="true"
        />
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import type { FunctionalComponent } from 'vue';
import type { StrapiBlockbusterItemComponent } from '~/apollo/types/types';
import HomePageBlockbusterSlide from '~/components/home-page/blockbuster/Slide.vue';

const INTERVAL_MS = 4000;

const props = defineProps<{
  id: string;
  items: StrapiBlockbusterItemComponent[];
}>();

const activeIndex = ref(0);

// Single timer reference — only one interval can exist at a time.
// `isCycling` tracks intent: true = auto-advance desired, false = paused by hover.
let timer: ReturnType<typeof setInterval> | null = null;
let isCycling = false;

// --- 3-slide rotation (reactive classes) ---

const leftIndex = computed(() =>
  activeIndex.value === 0 ? props.items.length - 1 : activeIndex.value - 1,
);

const rightIndex = computed(() =>
  activeIndex.value === props.items.length - 1 ? 0 : activeIndex.value + 1,
);

const TRANSITION = 'transition-transform duration-700 ease-in-out';

const slideClass = (index: number) => {
  if (index === activeIndex.value) {
    return `${TRANSITION} translate-x-0 z-30`;
  }
  if (index === leftIndex.value) {
    return `${TRANSITION} -translate-x-full z-10`;
  }
  if (index === rightIndex.value) {
    return `${TRANSITION} translate-x-full z-20`;
  }
  return 'hidden';
};

// --- auto-cycle (Flowbite pattern: cycle/pause pair) ---

const cycle = () => {
  if (props.items.length <= 1) return;
  clearTimer();
  timer = setInterval(() => {
    next();
  }, INTERVAL_MS);
};

const clearTimer = () => {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
};

// --- navigation ---
// Flowbite pattern: slideTo() is the single entry point for all navigation.
// If cycling is active (isCycling), clear and restart the interval so
// the next auto-advance is always a full INTERVAL_MS after the last interaction.
// If paused (hover), just change the slide without touching the timer.

const slideTo = (index: number) => {
  if (index === activeIndex.value) return;
  activeIndex.value = index;
  if (isCycling) {
    clearTimer();
    cycle();
  }
};

const next = () => {
  slideTo((activeIndex.value + 1) % props.items.length);
};

const prev = () => {
  slideTo((activeIndex.value - 1 + props.items.length) % props.items.length);
};

// --- hover pause / resume ---

const pause = () => {
  clearTimer();
  isCycling = false;
};

const resume = () => {
  isCycling = true;
  cycle();
};

// --- lifecycle (SSR-safe: onMounted only runs on client) ---

onMounted(() => {
  isCycling = true;
  cycle();
});
onUnmounted(() => {
  clearTimer();
  isCycling = false;
});

// --- indicator icons ---

const availableIcons = [
  defineAsyncComponent(
    () => import('~/assets/icons/mr-maintenance.svg?component'),
  ),
  defineAsyncComponent(() => import('~/assets/icons/gamepad.svg?component')),
  defineAsyncComponent(() => import('~/assets/icons/d-pad.svg?component')),
  defineAsyncComponent(() => import('~/assets/icons/circle.svg?component')),
];

const indicatorIcons = computed(() => {
  const icons: FunctionalComponent[] = [];
  for (let i = 0; i < props.items.length; ++i) {
    icons.push(availableIcons[i % availableIcons.length]);
  }
  return icons;
});
</script>
